import { create } from "zustand";
import { toast } from "sonner";
import type { IEvent } from "@/components/today/interfaces";

interface PendingDropData {
	event: IEvent;
	newStartDate: Date;
	newEndDate: Date;
}

interface DndState {
	// State
	draggedEvent: IEvent | null;
	isDragging: boolean;
	showConfirmation: boolean;
	pendingDropData: PendingDropData | null;

	// Actions
	startDrag: (event: IEvent) => void;
	endDrag: () => void;
	handleEventDrop: (
		date: Date,
		hour: number | undefined,
		minute: number | undefined,
		updateEvent: (event: IEvent) => void,
	) => void;
	handleConfirmDrop: (updateEvent: (event: IEvent) => void) => void;
	handleCancelDrop: () => void;
	setShowConfirmation: (show: boolean) => void;
}

const calculateNewDates = (
	event: IEvent,
	targetDate: Date,
	hour?: number,
	minute?: number,
) => {
	const originalStart = new Date(event.startDate);
	const originalEnd = new Date(event.endDate);
	const duration = originalEnd.getTime() - originalStart.getTime();

	const newStart = new Date(targetDate);
	if (hour !== undefined) {
		newStart.setHours(hour, minute || 0, 0, 0);
	} else {
		newStart.setHours(
			originalStart.getHours(),
			originalStart.getMinutes(),
			0,
			0,
		);
	}

	return {
		newStart,
		newEnd: new Date(newStart.getTime() + duration),
	};
};

const isSamePosition = (date1: Date, date2: Date) => {
	return date1.getTime() === date2.getTime();
};

/**
 * Zustand store for calendar event drag and drop functionality
 * Replaces the previous React context-based dnd-context
 */
export const useDndStore = create<DndState>((set, get) => ({
	// Initial state
	draggedEvent: null,
	isDragging: false,
	showConfirmation: false,
	pendingDropData: null,

	// Actions
	startDrag: (event: IEvent) => {
		set({ draggedEvent: event, isDragging: true });
	},

	endDrag: () => {
		set({ draggedEvent: null, isDragging: false });
	},

	handleEventDrop: (
		targetDate: Date,
		hour: number | undefined,
		minute: number | undefined,
		updateEvent: (event: IEvent) => void,
	) => {
		const { draggedEvent, showConfirmation, endDrag } = get();
		if (!draggedEvent) return;

		const { newStart, newEnd } = calculateNewDates(
			draggedEvent,
			targetDate,
			hour,
			minute,
		);
		const originalStart = new Date(draggedEvent.startDate);

		// Check if dropped in same position
		if (isSamePosition(originalStart, newStart)) {
			endDrag();
			return;
		}

		if (showConfirmation) {
			// Show confirmation dialog if user wants it
			set({
				pendingDropData: {
					event: draggedEvent,
					newStartDate: newStart,
					newEndDate: newEnd,
				},
			});
		} else {
			// Instantly update event if user doesn't want confirmation
			try {
				const updatedEvent = {
					...draggedEvent,
					startDate: newStart.toISOString(),
					endDate: newEnd.toISOString(),
				};
				updateEvent(updatedEvent);
				toast.success("Event updated successfully");
			} catch {
				toast.error("Failed to update event");
			}
			endDrag();
		}
	},

	handleConfirmDrop: (updateEvent: (event: IEvent) => void) => {
		const { pendingDropData, endDrag } = get();
		if (!pendingDropData) return;

		try {
			const updatedEvent = {
				...pendingDropData.event,
				startDate: pendingDropData.newStartDate.toISOString(),
				endDate: pendingDropData.newEndDate.toISOString(),
			};
			updateEvent(updatedEvent);
			toast.success("Event updated successfully");
		} catch {
			toast.error("Failed to update event");
		}

		// Reset states
		set({ pendingDropData: null });
		endDrag();
	},

	handleCancelDrop: () => {
		set({ pendingDropData: null });
		get().endDrag();
	},

	setShowConfirmation: (show: boolean) => {
		set({ showConfirmation: show });
	},
}));

